/**
 * Copyright 2022-2026 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import { useState } from "react";
import { Switch } from "react-router-dom";
import styled from "styled-components";
import { theme } from "@nordcloud/gnui";
import { isDev } from "~/config";
import { MOBILE_MAX_WIDTH } from "~/constants";
import { MenuBar, Header, Footer } from "~/layout";
import { RumRoute as Route } from "~/services/datadog";
import {
  ApiDocsPage,
  AccountsPage,
  AccountDetails,
  Dashboard,
  AccountCreate,
  ResourcesPage,
  ResourceGroupsPage,
  ResourceDetails,
  RolesPage,
  APIKeysPage,
  ResourceGroupCreate,
  ResourceGroupDetails,
  UsersPage,
  ResourceCreate,
  CustomActionsPage,
  CustomActionDetails,
  CustomActionCreate,
  PlansPage,
  PlanCreate,
  PlanDetails,
  NotificationGroupsPage,
  NotificationGroupDetails,
  NotificationGroupsCreate,
  EventsPage,
  EventDetails,
  MappingRulesPage,
  ApplicationsPage,
  ApplicationCreate,
  AuditLogsPage,
} from "~/views";
import { Reports } from "~/views/resources";
import {
  ResourcesPageV2,
  ResourceDetailsV2,
  ResourceReports,
} from "~/views/resources_v2";
import { ROUTES } from "./routes";

const layoutHorizontalSpacing = theme.spacing.spacing04;

export function GlobalSwitch() {
  const [isMenuExpanded, setIsMenuExpanded] = useState(false);

  return (
    <AppLayoutContainer>
      <ApplicationGrid $isMenuExpanded={isMenuExpanded}>
        <HeaderWrapper>
          <Header />
        </HeaderWrapper>
        <MenuBar onExpandChange={() => setIsMenuExpanded((prev) => !prev)} />
        <ApplicationWrapper>
          <Switch>
            <Route exact path={ROUTES.index} component={Dashboard} />
            <Route exact path={ROUTES.api} component={ApiDocsPage} />
            <Route exact path={ROUTES.events.index} component={EventsPage} />
            <Route
              exact
              path={ROUTES.events.details}
              component={EventDetails}
            />
            <Route
              exact
              path={ROUTES.resources.create}
              component={ResourceCreate}
            />
            <Route
              exact
              path={ROUTES.resources.index}
              component={ResourcesPage}
            />
            <Route
              exact
              path={ROUTES.resourcesV2.index}
              component={ResourcesPageV2}
            />
            <Route exact path={ROUTES.resources.reports} component={Reports} />
            {isDev && (
              <Route exact path="/v2/reports" component={ResourceReports} />
            )}
            <Route
              exact
              path={ROUTES.resources.details}
              component={ResourceDetails}
            />
            <Route
              exact
              path={ROUTES.resourcesV2.details}
              component={ResourceDetailsV2}
            />
            <Route
              exact
              path={ROUTES.accounts.create}
              component={AccountCreate}
            />
            <Route
              exact
              path={ROUTES.accounts.index}
              component={AccountsPage}
            />
            <Route
              exact
              path={ROUTES.accounts.details}
              component={AccountDetails}
            />
            <Route exact path={ROUTES.plans.index} component={PlansPage} />
            <Route exact path={ROUTES.plans.create} component={PlanCreate} />
            <Route exact path={ROUTES.plans.details} component={PlanDetails} />
            <Route
              exact
              path={ROUTES.customActions.create}
              component={CustomActionCreate}
            />
            <Route
              exact
              path={ROUTES.customActions.index}
              component={CustomActionsPage}
            />
            <Route
              exact
              path={ROUTES.customActions.details}
              component={CustomActionDetails}
            />

            <Route
              exact
              path={ROUTES.permissions.roles}
              component={RolesPage}
            />
            <Route
              exact
              path={ROUTES.permissions.apiKeys}
              component={APIKeysPage}
            />
            <Route
              exact
              path={ROUTES.permissions.users}
              component={UsersPage}
            />
            <Route
              exact
              path={ROUTES.resourceGroups.create}
              component={ResourceGroupCreate}
            />
            <Route
              exact
              path={ROUTES.resourceGroups.index}
              component={ResourceGroupsPage}
            />
            <Route
              exact
              path={ROUTES.resourceGroups.details}
              component={ResourceGroupDetails}
            />
            <Route
              exact
              path={ROUTES.notificationGroups.create}
              component={NotificationGroupsCreate}
            />
            <Route
              exact
              path={ROUTES.notificationGroups.index}
              component={NotificationGroupsPage}
            />
            <Route
              exact
              path={ROUTES.notificationGroups.details}
              component={NotificationGroupDetails}
            />
            <Route
              exact
              path={ROUTES.mappingRules.index}
              component={MappingRulesPage}
            />
            <Route
              exact
              path={ROUTES.auditLogs.index}
              component={AuditLogsPage}
            />
            {isDev && (
              <>
                <Route
                  exact
                  path={ROUTES.applications.index}
                  component={ApplicationsPage}
                />
                <Route
                  exact
                  path={ROUTES.applications.create}
                  component={ApplicationCreate}
                />
              </>
            )}
          </Switch>
        </ApplicationWrapper>
        <FooterWrapper>
          <Footer />
        </FooterWrapper>
      </ApplicationGrid>
    </AppLayoutContainer>
  );
}

// cache menu bar & layout dimensions
const menuWidth = theme.spacing.spacing07;
const expandedMenuWidth = "14rem";
const menuHorizontalPadding = theme.spacing.spacing03;

// `1st calc`: we need to add menuHorizontalPadding twice because we have
// paddingLeft and paddingRight on the menu element

const AppLayoutContainer = styled.div`
  flex: 1;
  min-width: 0;
  max-width: 100%;
  width: 100%;
  overflow-x: hidden;
`;

const ApplicationGrid = styled.div<{ $isMenuExpanded: boolean }>`
  display: grid;
  width: 100%;
  max-width: 100%;
  min-width: 0;
  overflow-x: hidden;
  gap: ${theme.spacing.spacing04} ${layoutHorizontalSpacing};
  transition: grid-template-columns 0.3s ease;
  --sidebar-content-offset: calc(
    ${menuWidth} + 2 * ${menuHorizontalPadding} + ${layoutHorizontalSpacing}
  );

  ${({ $isMenuExpanded }) => {
    const width = $isMenuExpanded ? expandedMenuWidth : menuWidth;

    // eslint-disable-next-line sonarjs/no-nested-template-literals
    return `
      grid-template-columns:
        calc(${width} + 2 * ${menuHorizontalPadding})
        minmax(0, 1fr);
      --sidebar-content-offset: calc(
        ${width} + 2 * ${menuHorizontalPadding} + ${layoutHorizontalSpacing}
      );
    `;
  }}

  grid-template-areas:
    "header header"
    ". application"
    ". footer";

  @media (max-width: ${MOBILE_MAX_WIDTH}px) {
    column-gap: ${theme.spacing.spacing02};
    row-gap: ${theme.spacing.spacing03};
    --sidebar-content-offset: calc(
      ${menuWidth} + 2 * ${menuHorizontalPadding} + ${theme.spacing.spacing02}
    );
  }
`;

const HeaderWrapper = styled.header`
  grid-area: header;
  grid-column: 1 / -1;
  min-width: 0;
  width: 100%;
  background-color: ${theme.color.background.ui01};
  border-radius: 0;
  box-sizing: border-box;
`;

const ApplicationWrapper = styled.div`
  grid-area: application;
  box-sizing: border-box;
  min-width: 0;
  width: 100%;
  padding-right: ${layoutHorizontalSpacing};

  @media (max-width: ${MOBILE_MAX_WIDTH}px) {
    padding-right: ${theme.spacing.spacing02};
    overflow-x: clip;
  }

  @media screen and (min-width: 2500px) {
    margin: 0 auto;
    max-width: 2500px;
  }
`;

const FooterWrapper = styled.div`
  grid-area: footer;
  padding-top: ${theme.spacing.spacing05};
  padding-right: ${layoutHorizontalSpacing};
  min-width: 0;
  width: 100%;

  @media (max-width: ${MOBILE_MAX_WIDTH}px) {
    padding-right: ${theme.spacing.spacing02};
  }

  @media screen and (min-width: 2500px) {
    padding-right: 0;
  }
`;
